import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useCallback, useState } from 'react';
import useLocalStorageSettings from './useLocalStorageSettings';
import { gql } from '@/modes/common/gql';

const GET_UI_SETTINGS = gql(`
  query GetUiSetting($key: String!) {
    uiSetting(key: $key)
  }
`);

const SAVE_UI_SETTINGS = gql(`
  mutation SaveUiSetting($key: String!, $value: JSON!) {
    saveUiSetting(key: $key, value: $value)
  }
`);

const useUiSettings = <T>(
  page: string,
  type: string
): [T | null, (value: T | null) => void, boolean] => {
  const client = useApolloClient();
  const serverKey = `${page}.${type}`;

  const [localStorageValue, setLocalStorageValue] = useLocalStorageSettings<T>(
    page,
    type
  );

  const [settings, setInMemorySettings] = useState<T | null>(localStorageValue);

  const { loading } = useQuery<{ uiSetting?: T }>(GET_UI_SETTINGS, {
    variables: { key: serverKey },
    onCompleted: (data) => {
      if (data.uiSetting) {
        setInMemorySettings(data.uiSetting);
      }
    },
  });

  const [saveSettings] = useMutation<boolean, { key: string; value: T | null }>(
    SAVE_UI_SETTINGS
  );

  const setSettings = useCallback(
    (value: T | null) => {
      setLocalStorageValue(value);
      setInMemorySettings(value);
      client.writeQuery({
        query: GET_UI_SETTINGS,
        variables: { key: serverKey },
        data: { uiSetting: value },
      });
      saveSettings({
        variables: {
          key: serverKey,
          value,
        },
      });
    },
    [client, serverKey, setLocalStorageValue, saveSettings]
  );

  return [settings, setSettings, loading];
};

export default useUiSettings;
